import React, { useEffect, useState, useCallback } from "react";
import adminAxios from "./adminAxios";
import { useAuth } from "../context/AuthContext"; 
import { 
  ShieldCheck, 
  Activity, 
  Database, 
  Users, 
  Clock, 
  DollarSign, 
  RefreshCw, 
  Zap, 
  Cpu,
  Terminal,
  BellRing
} from "lucide-react";

const colors = {
  primary: "#03f8d7",
  accent: "#ff2d75",
  deepBg: "#060608",
  surface: "rgba(13, 13, 18, 0.95)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  border: "rgba(255, 255, 255, 0.08)",
  success: "#10B981",
  glass: "rgba(255, 255, 255, 0.02)"
};

function AdminDashboard() {
  const { socket } = useAuth();
  
  // Extract active tenant identifier from storage for isolated context scoping
  const tenantId = localStorage.getItem("adminId") || "global";

  const [stats, setStats] = useState(null);
  const [regKey, setRegKey] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminAxios.get("/api/admin/admin-stats", { params: { tenantId } });
      setStats(res.data);
    } catch (e) {
      console.error("Telemetry Sync Failure:", e);
      setError("COMMUNICATION ERROR: Central Server Offline");
    }
  }, [tenantId]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await adminAxios.get("/api/admin/settings", { params: { tenantId } });
      setRegKey(res.data.registrationKey || "");
    } catch (err) {
      console.error("Registry Retrieval Error:", err);
    }
  }, [tenantId]);

  const handleUpdateKey = async () => {
    if (!regKey.trim()) return;
    setIsUpdating(true);
    try {
      await adminAxios.put("/api/admin/settings", { registrationKey: regKey, tenantId });
      alert("INTERFACE UPDATE: Station Access Protocols Synchronized.");
    } catch (err) {
      alert("AUTHORIZATION DENIED: Insufficient Security Clearance.");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSettings();

    if (socket) {
      const joinTenantAdmin = () => {
        socket.emit("join", { userId: "admin", role: "admin", tenantId });
      };

      if (socket.connected) joinTenantAdmin();
      socket.on("connect", joinTenantAdmin);

      socket.on("adminStatsUpdated", fetchStats);

      socket.on("newDepositRequest", (data) => {
        setNotification({
          message: `NEW UPLINK: ${data.userName} requested ${data.amount} ${data.currency}`,
          type: "deposit"
        });
        fetchStats();
        
        setTimeout(() => setNotification(null), 10000);
      });

      return () => {
        socket.off("connect", joinTenantAdmin);
        socket.off("adminStatsUpdated", fetchStats);
        socket.off("newDepositRequest");
      };
    }
  }, [fetchStats, fetchSettings, socket, tenantId]);

  const formatCurrency = (val) => `$${Number(val || 0).toLocaleString()}`;

  if (error) return <div style={styles.loadingScreen}>{error}</div>;

  if (!stats) return (
    <div style={styles.loadingScreen}>
      <RefreshCw className="animate-spin" size={32} style={{ color: colors.primary, marginBottom: '20px' }} />
      BOOTING CORE INTERFACE...
    </div>
  );

  return (
    <div className="admin-page" data-tenant-scope={tenantId}>
      <style>
        {`
          .admin-page {
            min-height: 100vh;
            background-color: ${colors.deepBg};
            color: ${colors.textMain};
            font-family: 'Inter', sans-serif;
            padding-bottom: 60px;
          }

          .top-nav {
            background: ${colors.surface};
            padding: 15px 40px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid ${colors.border};
            backdrop-filter: blur(15px);
            position: sticky;
            top: 0;
            z-index: 100;
          }

          .brand-box {
            display: flex;
            align-items: center;
            gap: 15px;
          }

          .status-tag {
            background: rgba(16, 185, 129, 0.1);
            border: 1px solid rgba(16, 185, 129, 0.2);
            padding: 4px 10px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 9px;
            font-weight: 800;
            letter-spacing: 1px;
            color: ${colors.success};
          }

          .pulse-indicator {
            width: 6px;
            height: 6px;
            background: ${colors.success};
            border-radius: 50%;
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0% { opacity: 1; box-shadow: 0 0 0 0px rgba(16, 185, 129, 0.4); }
            70% { opacity: 0.5; box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
            100% { opacity: 1; box-shadow: 0 0 0 0px rgba(16, 185, 129, 0); }
          }

          .control-hub {
            background: ${colors.glass};
            border: 1px solid ${colors.border};
            padding: 8px 16px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 20px;
          }

          .key-input {
            background: #000;
            border: 1px solid ${colors.border};
            color: ${colors.primary};
            padding: 8px 15px;
            border-radius: 8px;
            font-family: 'JetBrains Mono', monospace;
            font-size: 12px;
            text-align: center;
            outline: none;
            width: 180px;
            transition: 0.3s;
          }

          .key-input:focus {
            border-color: ${colors.primary};
            box-shadow: 0 0 15px rgba(3, 248, 215, 0.1);
          }

          .update-btn {
            background: ${colors.primary};
            color: #000;
            border: none;
            padding: 9px 20px;
            border-radius: 8px;
            font-weight: 900;
            font-size: 10px;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .update-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 5px 20px rgba(3, 248, 215, 0.3);
          }

          .alert-popup {
            position: fixed;
            bottom: 30px;
            right: 30px;
            background: ${colors.surface};
            border: 1px solid ${colors.primary};
            padding: 20px;
            border-radius: 12px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.5), 0 0 20px ${colors.primary}20;
            z-index: 1000;
            display: flex;
            align-items: center;
            gap: 15px;
            animation: slideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            max-width: 400px;
            backdrop-filter: blur(10px);
          }

          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          .main-container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 40px;
          }

          .grid-layout {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 30px;
          }

          .section-header {
            margin-bottom: 30px;
            display: flex;
            align-items: center;
            gap: 12px;
            opacity: 0.8;
          }
        `}
      </style>

      {notification && (
        <div className="alert-popup">
          <div style={{ background: colors.primary, padding: '10px', borderRadius: '50%', color: '#000' }}>
            <BellRing size={20} className="animate-bounce" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '10px', fontWeight: '900', color: colors.primary, letterSpacing: '2px' }}>SYSTEM ALERT</div>
            <div style={{ fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>{notification.message}</div>
          </div>
          <button 
            onClick={() => setNotification(null)}
            style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
      )}

      <nav className="top-nav">
        <div className="brand-box">
          <div style={{ background: colors.primary, padding: '8px', borderRadius: '10px' }}>
            <Cpu size={20} color="#000" />
          </div>
          <div>
            <h1 style={{ fontSize: "16px", fontWeight: "900", letterSpacing: "3px", margin: 0 }}>SYSTEM COMMAND</h1>
            <div className="status-tag">
              <div className="pulse-indicator" /> NETWORK ENCRYPTED
            </div>
          </div>
        </div>

        <div className="control-hub">
          <div style={{ textAlign: "right", borderRight: `1px solid ${colors.border}`, paddingRight: "15px" }}>
            <div style={{ fontSize: "9px", fontWeight: "900", color: colors.primary, letterSpacing: "1px" }}>REGISTRY KEY</div>
            <div style={{ fontSize: "8px", color: colors.textMuted }}>NODE ACCESS CONTROL</div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input 
              className="key-input"
              value={regKey}
              onChange={(e) => setRegKey(e.target.value)}
              placeholder="UNSPECIFIED"
            />
            <button className="update-btn" onClick={handleUpdateKey} disabled={isUpdating}>
              {isUpdating ? <RefreshCw className="animate-spin" size={14}/> : <Zap size={14} />}
              {isUpdating ? "SYNCING" : "COMMIT"}
            </button>
          </div>
        </div>
      </nav>

      <div className="main-container">
        <div className="section-header">
          <Terminal size={18} color={colors.primary} />
          <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase' }}>Real-time Telemetry Metrics</span>
        </div>

        <div className="grid-layout">
          <StatCard icon={<Database size={20}/>} title="Asset Inventory" value={stats.totalProducts || 0} unit="SKU" color={colors.primary} />
          <StatCard icon={<Activity size={20}/>} title="Operations Flux" value={stats.totalOrders || 0} unit="TRANSACTIONS" color="#6366f1" />
          <StatCard icon={<Users size={20}/>} title="Active Terminals" value={stats.totalUsers || 0} unit="NODES" color="#a855f7" />
          <StatCard 
            icon={<Clock size={20}/>} 
            title="Authorization Queue" 
            value={stats.pendingApprovals || 0} 
            unit="PENDING" 
            color={stats.pendingApprovals > 0 ? colors.accent : colors.textMuted} 
          />
          <StatCard icon={<ShieldCheck size={20}/>} title="Gross Volume" value={formatCurrency(stats.totalCapitalFlow)} unit="USD" color={colors.success} />
          <StatCard icon={<DollarSign size={20}/>} title="Protocol Yield" value={formatCurrency(stats.totalSystemYield)} unit="USD" color="#0ea5e9" />
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, title, value, unit, color }) {
  return (
    <div style={{
      background: colors.surface,
      padding: "30px",
      borderRadius: "24px",
      border: `1px solid ${colors.border}`,
      position: 'relative',
      overflow: 'hidden',
      transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'default'
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.borderColor = color + '40';
      e.currentTarget.style.transform = 'translateY(-5px)';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.borderColor = colors.border;
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div style={{ color: color, background: `${color}15`, padding: '12px', borderRadius: '14px' }}>
          {icon}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '9px', fontWeight: '900', color: color, letterSpacing: '1.5px', textTransform: 'uppercase' }}>{unit}</div>
        </div>
      </div>
      
      <div style={{ fontSize: '12px', fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>
        {title}
      </div>
      
      <div style={{ fontSize: '32px', fontWeight: '900', color: '#fff', letterSpacing: '-1px' }}>
        {value}
      </div>

      <div style={{ 
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        width: '100%', 
        height: '4px', 
        background: `linear-gradient(90deg, ${color}, transparent)` 
      }} />
    </div>
  );
}

const styles = {
  loadingScreen: { 
    height: "100vh", 
    backgroundColor: colors.deepBg, 
    display: "flex", 
    flexDirection: "column", 
    alignItems: "center", 
    justifyContent: "center", 
    fontSize: "11px", 
    fontWeight: "900", 
    color: colors.textMuted, 
    letterSpacing: "3px" 
  },
};

export default AdminDashboard;